import type { CanvasPlace } from "../graph/canvasEditing";
import type { WorkflowRole } from "../model/workflowDraft";

/**
 * A palette item dragged onto the graph (AU-6): a step or a Workflow version. The drag
 * carries it as JSON under the editor's own type, so nothing else dropped there is taken.
 */
export interface PaletteItem {
  readonly kind: "step" | "workflow";
  readonly reference: string;
  readonly name: string;
}

const DRAG_TYPE = "application/x-dmw-process-item";

export function writePaletteDrag(transfer: DataTransfer, item: PaletteItem): void {
  transfer.setData(DRAG_TYPE, JSON.stringify(item));
  transfer.effectAllowed = "copy";
}

/**
 * What a drop on the editor's canvas adds: a palette item, as an input step when dropped
 * above the pipeline and as a pipeline step otherwise; `null` for anything else.
 */
export function paletteDrop(transfer: DataTransfer, place: CanvasPlace): { readonly item: PaletteItem; readonly role: WorkflowRole } | null {
  const item = readPaletteDrag(transfer);
  return item === null ? null : { item, role: place === "above_pipeline" ? "input" : "main" };
}

export function readPaletteDrag(transfer: DataTransfer): PaletteItem | null {
  const text = transfer.getData(DRAG_TYPE);
  if (text === "") {
    return null;
  }
  try {
    const value: unknown = JSON.parse(text);
    if (typeof value !== "object" || value === null) {
      return null;
    }
    const { kind, reference, name } = value as Record<string, unknown>;
    return (kind === "step" || kind === "workflow") && typeof reference === "string" && typeof name === "string" ? { kind, reference, name } : null;
  } catch {
    return null;
  }
}
